/**
 * SIPInfoDrawer - Storm Impact Predictions Info Drawer
 *
 * Right-side popup drawer that displays detailed information when clicking
 * on a map feature. Shows locale info, weather threats, and forecast data.
 */

import * as React from "react";
import { useCallback, useMemo, useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Card,
  CardContent,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Info,
  CloudRain,
  Snowflake,
  Wind,
  Thermometer,
  Zap,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  ForecastEvolutionChart,
  createEvolutionLineConfigs,
} from "@/components/charts";
import { useForecastTrends } from "@/hooks/api";
import type { ForecastDataItem, ForecastVariableData } from "@/api/types";
import type {
  StormImpactModel,
  WeatherModel,
  StormImpactVariable,
} from "@/types";
import { getThreatBadgeClass } from "@/utils/mapStyling";
import { format } from "date-fns";

// ============================================================================
// Types
// ============================================================================

export interface SIPInfoDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when drawer open state changes */
  onOpenChange: (open: boolean) => void;
  /** GeoJSON feature data to display */
  featureData?: any;
  /** Forecast data for the selected feature */
  forecastData?: ForecastDataItem | null;
  /** Selected storm impact model */
  stormImpactModel?: StormImpactModel;
  /** Selected weather model */
  weatherModel?: WeatherModel;
  /** Selected date */
  selectedDate?: Date;
  /** Selected run */
  selectedRun?: Date;
  /** Available variables for display */
  availableVariables?: StormImpactVariable[];
}

export interface DrawerState {
  open: boolean;
  selectedFeature: any | null;
  forecastData: ForecastDataItem | null;
}

// ============================================================================
// Threat Icon Mapping
// ============================================================================

const THREAT_ICONS: Record<string, React.ReactNode> = {
  snow: <Snowflake className="h-4 w-4" />,
  wind: <Wind className="h-4 w-4" />,
  rain: <CloudRain className="h-4 w-4" />,
  ice: <Thermometer className="h-4 w-4" />,
  thunderstorm: <Zap className="h-4 w-4" />,
};

const getThreatIcon = (slug: string): React.ReactNode => {
  const lowerSlug = slug.toLowerCase();
  for (const [key, icon] of Object.entries(THREAT_ICONS)) {
    if (lowerSlug.includes(key)) return icon;
  }
  return <CloudRain className="h-4 w-4" />;
};

// ============================================================================
// Stylish Divider Component - Pill/button style with solid background
// ============================================================================

const StylishDivider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="relative my-4">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-dashed border-gray-300 dark:border-gray-600" />
    </div>
    <div className="relative flex justify-center">
      <div className="px-4 py-1 text-base font-semibold rounded-xl shadow-sm bg-white text-gray-700 dark:bg-zinc-900 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
        {children}
      </div>
    </div>
  </div>
);

// ============================================================================
// Weather Threats Component
// ============================================================================

interface ThreatIndicatorsProps {
  threats: ForecastVariableData[];
}

const ThreatIndicators: React.FC<ThreatIndicatorsProps> = ({ threats }) => {
  if (threats.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-semibold">
          Predicted Significant Weather Threats ({threats.length})
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 cursor-help text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-sm">
                These weather threats are predicted to have significant impact
                in this area.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="threat-indicators-container">
        {threats.map((threat) => (
          <span
            key={threat.variable_id}
            className={`threat-badge ${getThreatBadgeClass(
              threat.variable_slug
            )}`}
          >
            {getThreatIcon(threat.variable_slug)}
            <span className="threat-badge-text">{threat.variable_name}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Variable Selection Dropdown Component
// ============================================================================

interface VariablesByUnits {
  [unitsKey: string]: Array<{
    slug: string;
    name: string;
    units: string | null;
    order: number;
  }>;
}

interface VariableSelectionDropdownProps {
  availableVariables: string[];
  selectedVariables: string[];
  onSelectedVariablesChange: (variables: string[]) => void;
  stormImpactModel?: StormImpactModel;
}

const VariableSelectionDropdown: React.FC<VariableSelectionDropdownProps> = ({
  availableVariables,
  selectedVariables,
  onSelectedVariablesChange,
  stormImpactModel,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Format variable name
  const formatVariableName = (slug: string): string => {
    const names: Record<string, string> = {
      estimate25th: "25th Percentile",
      predicted_median: "Median",
      estimate75th: "75th Percentile",
      best_estimate: "Best Estimate",
      estimate5th: "5th Percentile",
      estimate10th: "10th Percentile",
      estimate90th: "90th Percentile",
      estimate95th: "95th Percentile",
    };
    return (
      names[slug] ||
      slug.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  // Group variables by units
  const variablesByUnits = useMemo<VariablesByUnits>(() => {
    const groups: VariablesByUnits = {};

    availableVariables.forEach((slug) => {
      const variable = stormImpactModel?.variables?.find(
        (v) => v.variable_slug === slug
      );
      const units = variable?.variable_units || null;
      const unitsKey = units || "No Units";

      if (!groups[unitsKey]) {
        groups[unitsKey] = [];
      }

      groups[unitsKey].push({
        slug,
        name: variable?.variable_name || formatVariableName(slug),
        units,
        order: variable?.variable_order || 999,
      });
    });

    // Sort variables within each group
    Object.keys(groups).forEach((unitsKey) => {
      groups[unitsKey].sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.name.localeCompare(b.name);
      });
    });

    return groups;
  }, [availableVariables, stormImpactModel]);

  const handleSelectAll = () => {
    onSelectedVariablesChange(availableVariables);
  };

  const handleClear = () => {
    onSelectedVariablesChange([]);
  };

  const handleSelectDefault = () => {
    const defaultVariables = [
      "estimate90th",
      "predicted_median",
      "estimate75th",
      "best_estimate",
    ].filter((v) => availableVariables.includes(v));
    onSelectedVariablesChange(defaultVariables);
  };

  const handleSelectByUnits = (unitsKey: string) => {
    const variablesInGroup =
      variablesByUnits[unitsKey]?.map((v) => v.slug) || [];
    const allSelected = variablesInGroup.every((v) =>
      selectedVariables.includes(v)
    );

    if (allSelected) {
      // Deselect all in this group
      onSelectedVariablesChange(
        selectedVariables.filter((v) => !variablesInGroup.includes(v))
      );
    } else {
      // Select all in this group (replace current selection to keep same units)
      onSelectedVariablesChange(variablesInGroup);
    }
  };

  const handleVariableToggle = (variable: string, checked: boolean) => {
    if (checked) {
      // Get the units of the variable being added
      const newVariable = stormImpactModel?.variables?.find(
        (v) => v.variable_slug === variable
      );
      const newVariableUnits = newVariable?.variable_units || null;

      if (selectedVariables.length === 0) {
        onSelectedVariablesChange([variable]);
        return;
      }

      // Check if current selection has same units
      const currentVariable = stormImpactModel?.variables?.find((v) =>
        selectedVariables.includes(v.variable_slug)
      );
      const currentUnits = currentVariable?.variable_units || null;

      if (newVariableUnits !== currentUnits) {
        // Different units - replace selection
        onSelectedVariablesChange([variable]);
      } else {
        // Same units - add to selection
        onSelectedVariablesChange([...selectedVariables, variable]);
      }
    } else {
      onSelectedVariablesChange(
        selectedVariables.filter((v) => v !== variable)
      );
    }
  };

  if (availableVariables.length === 0) return null;

  return (
    <div className="mb-4">
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 border rounded-lg transition-all border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 text-gray-700 dark:text-gray-200"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Variables:</span>
          <span className="text-xs px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            {selectedVariables.length} selected
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="mt-2 border rounded-lg p-3 space-y-3 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleSelectAll}
              className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
            >
              Select All
            </button>
            <button
              onClick={handleClear}
              className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
            >
              Clear
            </button>
            <button
              onClick={handleSelectDefault}
              className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
            >
              Default
            </button>
          </div>

          {/* Variables Grouped by Units */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {Object.entries(variablesByUnits).map(([unitsKey, variables]) => {
              const allSelected = variables.every((v) =>
                selectedVariables.includes(v.slug)
              );
              const someSelected = variables.some((v) =>
                selectedVariables.includes(v.slug)
              );

              return (
                <div key={unitsKey} className="space-y-2">
                  {/* Unit Group Header */}
                  <div className="flex items-center justify-between px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                        Units: {unitsKey}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                        ({variables.length} parameters)
                      </span>
                    </div>
                    <button
                      onClick={() => handleSelectByUnits(unitsKey)}
                      className={`text-xs px-2 py-0.5 rounded transition-colors ${
                        allSelected
                          ? "bg-gray-500 text-white hover:bg-gray-600"
                          : someSelected
                          ? "bg-yellow-500 text-white hover:bg-yellow-600"
                          : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-400 dark:hover:bg-gray-500"
                      }`}
                    >
                      Select All
                    </button>
                  </div>

                  {/* Variables in this unit group */}
                  <div className="grid grid-cols-1 gap-1 pl-2">
                    {variables.map((variable) => (
                      <label
                        key={variable.slug}
                        className="flex items-center justify-between p-1.5 rounded cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedVariables.includes(variable.slug)}
                            onChange={(e) =>
                              handleVariableToggle(
                                variable.slug,
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 text-gray-600 focus:ring-gray-400"
                          />
                          <span className="text-sm font-medium">
                            {variable.name}
                          </span>
                        </div>
                        {variable.units && (
                          <span className="text-xs px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400">
                            {variable.units}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Forecast Evolution Section Component
// ============================================================================

interface ForecastEvolutionSectionProps {
  nodeId: string;
  modelId: string;
  weatherModelId: string;
  forecastDate: string;
  stormImpactModel?: StormImpactModel;
  units?: string;
}

const ForecastEvolutionSection: React.FC<ForecastEvolutionSectionProps> = ({
  nodeId,
  modelId,
  weatherModelId,
  forecastDate,
  stormImpactModel,
}) => {
  const { data, availableVariables, loading, error } = useForecastTrends({
    nodeId,
    modelId,
    weatherModelId,
    forecastDate,
    stormImpactModel,
  });
  const [selectedVariables, setSelectedVariables] = useState<string[]>([
    "estimate90th",
    "predicted_median",
    "estimate75th",
    "best_estimate",
  ]);

  // Update selected variables to only include available ones
  useEffect(() => {
    if (availableVariables.length > 0) {
      const validSelected = selectedVariables.filter((v) =>
        availableVariables.includes(v)
      );
      if (validSelected.length === 0) {
        // Select defaults if available
        const defaults = [
          "estimate90th",
          "predicted_median",
          "estimate75th",
          "best_estimate",
        ].filter((v) => availableVariables.includes(v));
        if (defaults.length > 0) {
          setSelectedVariables(defaults);
        } else if (availableVariables.length > 0) {
          setSelectedVariables([availableVariables[0]]);
        }
      } else if (validSelected.length !== selectedVariables.length) {
        setSelectedVariables(validSelected);
      }
    }
  }, [availableVariables]);

  // Create line configs for selected variables
  const lineConfigs = useMemo(() => {
    const availableSelected = selectedVariables.filter((v) =>
      data.some((d) => d[v] !== undefined && d[v] !== null)
    );
    return createEvolutionLineConfigs(availableSelected);
  }, [data, selectedVariables]);

  // Get units for Y-axis label from selected variables
  const yAxisUnits = useMemo(() => {
    if (selectedVariables.length === 0) return "Value";
    const firstVariable = stormImpactModel?.variables?.find((v) =>
      selectedVariables.includes(v.variable_slug)
    );
    return firstVariable?.variable_units || "Value";
  }, [selectedVariables, stormImpactModel]);

  // Calculate statistics for selected variables
  const statistics = useMemo(() => {
    if (data.length === 0 || selectedVariables.length === 0) return [];

    return selectedVariables
      .map((variableSlug) => {
        const variable = stormImpactModel?.variables?.find(
          (v) => v.variable_slug === variableSlug
        );
        const variableName =
          variable?.variable_name || formatVariableNameHelper(variableSlug);
        const units = variable?.variable_units || "";

        let currentValue: number | null = null;
        let previousValue: number | null = null;
        let maxValue = -Infinity;
        let minValue = Infinity;

        if (data.length >= 1) {
          const val = data[0][variableSlug];
          currentValue = typeof val === "number" ? val : null;
        }
        if (data.length >= 2) {
          const val = data[1][variableSlug];
          previousValue = typeof val === "number" ? val : null;
        }

        data.forEach((dataPoint) => {
          const value = dataPoint[variableSlug];
          if (typeof value === "number" && !isNaN(value)) {
            if (value > maxValue) maxValue = value;
            if (value < minValue) minValue = value;
          }
        });

        let change: number | null = null;
        let percentChange: number | null = null;
        if (currentValue !== null && previousValue !== null) {
          change = currentValue - previousValue;
          percentChange =
            previousValue !== 0 ? (change / previousValue) * 100 : 0;
        }

        return {
          variable: variableSlug,
          variableName,
          units,
          currentValue,
          previousValue,
          change,
          percentChange,
          maxValue: maxValue !== -Infinity ? maxValue : null,
          minValue: minValue !== Infinity ? minValue : null,
        };
      })
      .filter((stat) => stat.currentValue !== null);
  }, [data, selectedVariables, stormImpactModel]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="flex items-center justify-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-purple-500 animate-pulse" />
          <div className="w-4 h-4 rounded-full bg-purple-500 animate-pulse animation-delay-200" />
          <div className="w-4 h-4 rounded-full bg-purple-500 animate-pulse animation-delay-400" />
        </div>
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Loading forecast evolution...
        </p>
      </div>
    );
  }

  if (error || data.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
          <span className="text-xl">📈</span>
        </div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
          No Trend Data Available
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No forecast trend data found for different model runs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Title with Info Icon */}
      <div className="text-sm font-medium text-gray-700 dark:text-gray-200 text-center flex items-center justify-center gap-2">
        <span>
          Model Run Evolution
          <span className="text-gray-400 dark:text-gray-500">
            {" "}
            ({data.length} runs)
          </span>
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 cursor-help text-gray-400 hover:text-gray-500" />
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="text-sm">
                This chart shows how the forecast has evolved across different
                model runs for the selected date. Each point represents a
                different model run time, allowing you to see forecast stability
                and changes in predictions over time.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Variable Selection Dropdown */}
      <VariableSelectionDropdown
        availableVariables={availableVariables}
        selectedVariables={selectedVariables}
        onSelectedVariablesChange={setSelectedVariables}
        stormImpactModel={stormImpactModel}
      />

      {/* Chart */}
      {selectedVariables.length > 0 ? (
        <>
          <ForecastEvolutionChart
            data={data}
            lines={lineConfigs}
            height={300}
            yAxisLabel={yAxisUnits}
            showGrid={true}
            showTooltip={true}
            className="w-full"
          />

          {/* Statistics */}
          {statistics.length > 0 && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700" />

              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1">
                  <span>Variable Statistics</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 cursor-help text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        <p className="text-sm">
                          Key statistics for each variable showing the most
                          recent prediction, changes since last update, and peak
                          values.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Statistics Grid */}
                <div
                  className="grid gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(
                      statistics.length,
                      2
                    )}, minmax(0, 1fr))`,
                  }}
                >
                  {statistics.map((stat) => (
                    <Card
                      key={stat.variable}
                      className={`border-l-2 py-2 ${
                        stat.change && stat.change > 0
                          ? "border-l-red-500"
                          : stat.change && stat.change < 0
                          ? "border-l-green-500"
                          : "border-l-gray-500"
                      }`}
                    >
                      <CardContent className="p-2">
                        {/* Variable Name & Units */}
                        <div className="flex items-start justify-between mb-1.5">
                          <CardTitle className="text-xs font-semibold leading-tight pr-1">
                            {stat.variableName}
                          </CardTitle>
                          {stat.units && (
                            <span className="text-xs px-1 py-0.5 rounded flex-shrink-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              {stat.units}
                            </span>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="space-y-0.5 text-xs">
                          {/* Most Recent Value */}
                          <div className="flex justify-between items-baseline">
                            <span className="text-gray-500 dark:text-gray-400">
                              Recent:
                            </span>
                            <span className="font-bold text-gray-900 dark:text-white">
                              {stat.currentValue !== null
                                ? Number.isInteger(stat.currentValue)
                                  ? stat.currentValue.toLocaleString()
                                  : stat.currentValue.toFixed(1)
                                : "—"}
                            </span>
                          </div>

                          {/* Change */}
                          {stat.change !== null &&
                            stat.percentChange !== null && (
                              <div className="flex justify-between items-baseline">
                                <span className="text-gray-500 dark:text-gray-400">
                                  Change:
                                </span>
                                <span
                                  className={`font-medium ${
                                    stat.change > 0
                                      ? "text-red-600 dark:text-red-400"
                                      : stat.change < 0
                                      ? "text-green-600 dark:text-green-400"
                                      : "text-gray-600 dark:text-gray-400"
                                  }`}
                                >
                                  {stat.change > 0 ? "+" : ""}
                                  {Number.isInteger(stat.change)
                                    ? stat.change.toLocaleString()
                                    : stat.change.toFixed(1)}
                                  {Math.abs(stat.percentChange) > 0.1 && (
                                    <span className="text-xs ml-0.5 opacity-75">
                                      ({stat.percentChange > 0 ? "+" : ""}
                                      {stat.percentChange.toFixed(1)}%)
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}

                          {/* Peak Value */}
                          {stat.maxValue !== null && (
                            <div className="flex justify-between items-baseline">
                              <span className="text-gray-500 dark:text-gray-400">
                                Peak:
                              </span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {Number.isInteger(stat.maxValue)
                                  ? stat.maxValue.toLocaleString()
                                  : stat.maxValue.toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <span className="text-xl">📊</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            No Variables Selected
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Please select at least one variable to display in the chart.
          </p>
        </div>
      )}
    </div>
  );
};

// Helper to format variable names
function formatVariableNameHelper(slug: string): string {
  const names: Record<string, string> = {
    estimate25th: "25th Percentile",
    predicted_median: "Median",
    estimate75th: "75th Percentile",
    best_estimate: "Best Estimate",
    estimate5th: "5th Percentile",
    estimate10th: "10th Percentile",
    estimate90th: "90th Percentile",
    estimate95th: "95th Percentile",
  };
  return (
    names[slug] ||
    slug.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

// ============================================================================
// Main SIPInfoDrawer Component
// ============================================================================

export function SIPInfoDrawer({
  open,
  onOpenChange,
  featureData,
  forecastData,
  stormImpactModel,
  weatherModel,
  selectedDate,
  selectedRun,
}: SIPInfoDrawerProps) {
  // Extract feature information
  const featureName =
    featureData?.properties?.name ||
    featureData?.properties?.area_name ||
    "Geographic Feature";

  // Build description from context
  const description = useMemo(() => {
    const parts: string[] = [];

    if (selectedDate) {
      const formattedDate = selectedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      parts.push(`Forecast Date: ${formattedDate}`);
    }

    if (stormImpactModel?.model_name) {
      parts.push(`Model: ${stormImpactModel.model_name}`);
    }

    if (weatherModel?.name) {
      parts.push(`Weather: ${weatherModel.name}`);
    } else if ((weatherModel as any)?.weather_model_name) {
      parts.push(`Weather: ${(weatherModel as any).weather_model_name}`);
    }

    if (selectedRun) {
      const formattedRun = selectedRun.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      parts.push(`Run: ${formattedRun}`);
    }

    return parts.join(" • ");
  }, [selectedDate, stormImpactModel, weatherModel, selectedRun]);

  // Extract weather threats from forecast data
  const weatherThreats = useMemo(() => {
    if (!forecastData?.variable_values) return [];

    const varValues = forecastData.variable_values;

    return varValues.filter(
      (v) => v.variable_type === "threat" && parseFloat(String(v.value)) === 1
    );
  }, [forecastData]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={true}>
      <SheetContent
        side="right"
        className="w-[60%] sm:max-w-[50%] p-0 flex flex-col"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="text-xl font-bold text-gray-900 dark:text-white">
            {featureName}
          </SheetTitle>
          <SheetDescription className="text-sm text-gray-500 dark:text-gray-400">
            {description}
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-3 space-y-6">
            {/* Weather Threats Section */}
            {weatherThreats.length > 0 && (
              <>
                <StylishDivider>Weather Threats</StylishDivider>
                <Card>
                  <CardContent className="p-4">
                    <ThreatIndicators threats={weatherThreats} />
                  </CardContent>
                </Card>
              </>
            )}

            {/* Forecast Evolution Section */}
            {forecastData &&
              featureData?.properties?.id &&
              stormImpactModel?.model_id &&
              weatherModel?.id &&
              selectedDate && (
                <>
                  <StylishDivider>Forecast Evolution</StylishDivider>
                  <Card>
                    <CardContent className="p-4">
                      <ForecastEvolutionSection
                        nodeId={featureData.properties.id}
                        modelId={stormImpactModel.model_id}
                        weatherModelId={weatherModel.id}
                        forecastDate={format(selectedDate, "yyyy-MM-dd")}
                        stormImpactModel={stormImpactModel}
                        units={
                          forecastData.variable_values?.find(
                            (v) => v.variable_slug === "best_estimate"
                          )?.variable_units || "Customers"
                        }
                      />
                    </CardContent>
                  </Card>
                </>
              )}

            {/* No Data Message */}
            {!forecastData && featureData && (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📊</span>
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  No Forecast Data
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No forecast data available for this location with the current
                  filters.
                </p>
              </div>
            )}

            {/* No Feature Selected Message */}
            {!featureData && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗺️</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Feature Selected
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Click on a map polygon to view its detailed information
                </p>
                <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1">
                  <p>• Feature properties will appear here</p>
                  <p>• Forecast data will be displayed</p>
                  <p>• Weather threats will be shown</p>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Hook for managing drawer state
// ============================================================================

export function useSIPDrawerState() {
  const [state, setState] = useState<DrawerState>({
    open: false,
    selectedFeature: null,
    forecastData: null,
  });

  const openDrawerWithFeature = useCallback(
    (feature: any, forecastData?: ForecastDataItem | null) => {
      setState({
        open: true,
        selectedFeature: feature,
        forecastData: forecastData || null,
      });
    },
    []
  );

  const closeDrawer = useCallback(() => {
    setState({
      open: false,
      selectedFeature: null,
      forecastData: null,
    });
  }, []);

  const setOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        closeDrawer();
      } else {
        setState((prev) => ({ ...prev, open: true }));
      }
    },
    [closeDrawer]
  );

  return {
    open: state.open,
    selectedFeature: state.selectedFeature,
    forecastData: state.forecastData,
    openDrawerWithFeature,
    closeDrawer,
    setOpen,
  };
}

export default SIPInfoDrawer;
